// Cave84 — Wishlist hooks (Phase 5 Week 15)
// TanStack Query wrappers around services/wishlist.ts.

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from "@tanstack/react-query";
import type { WishlistEntry } from "@cave84/shared-types";
import {
  createWishlistEntries,
  createWishlistEntry,
  deleteWishlistEntry,
  findWishlistMatchForWine,
  listActiveWishlistEntries,
  listWishlistEntries,
  markWishlistAcquired,
  updateWishlistEntry,
  type WishlistCreateInput,
  type WishlistUpdateInput,
} from "@/services/wishlist";
import { qk } from "@/services/firestore";

// ---------------------------------------------------------------------------
// Reads
// ---------------------------------------------------------------------------

/** All wishlist entries (active + acquired + abandoned). */
export function useWishlistEntries(): UseQueryResult<WishlistEntry[]> {
  return useQuery({
    queryKey: qk.wishlist.all,
    queryFn: listWishlistEntries,
    staleTime: 5 * 60 * 1000,
  });
}

/** Active entries only — used on WishlistPage default view. */
export function useActiveWishlistEntries(): UseQueryResult<WishlistEntry[]> {
  return useQuery({
    queryKey: [...qk.wishlist.all, "active"],
    queryFn: listActiveWishlistEntries,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Find active wishlist matches for a wine+year.
 * Used by AddBottlePage (F9.6).
 * Not cached aggressively — point-in-time check.
 */
export function useWishlistMatchForWine(
  wineId: string | undefined,
  vintageYear: number | undefined
): UseQueryResult<WishlistEntry[]> {
  return useQuery({
    queryKey: wineId
      ? qk.wishlist.byWine(wineId)
      : qk.wishlist.byWine("__noop__"),
    queryFn: () =>
      wineId
        ? findWishlistMatchForWine(wineId, vintageYear)
        : Promise.resolve([]),
    enabled: !!wineId,
    staleTime: 0,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useCreateWishlistEntry(): UseMutationResult<
  WishlistEntry,
  Error,
  WishlistCreateInput
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createWishlistEntry,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.wishlist.all });
    },
  });
}

/** Bulk add (Inventory selection). Resolves to the number of new entries created. */
export function useCreateWishlistEntries(): UseMutationResult<
  number,
  Error,
  WishlistCreateInput[]
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createWishlistEntries,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.wishlist.all });
    },
  });
}

export function useUpdateWishlistEntry(): UseMutationResult<
  void,
  Error,
  { id: string; input: WishlistUpdateInput }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }) => updateWishlistEntry(id, input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.wishlist.all });
    },
  });
}

export function useMarkWishlistAcquired(): UseMutationResult<
  void,
  Error,
  { id: string; bottleIds?: string[] }
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, bottleIds }) => markWishlistAcquired(id, bottleIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.wishlist.all });
    },
  });
}

export function useDeleteWishlistEntry(): UseMutationResult<void, Error, string> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteWishlistEntry,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: qk.wishlist.all });
    },
  });
}
